import "./CallDialogs.css";
import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Typography,
  Collapse,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { RotateCw } from "lucide-react";
import {
  DEFAULT_PRACTICE_TIMEZONE,
  formatShortInPracticeTimezone,
} from "../../practiceTimezone";
import InfoTooltip from "../../components/common/InfoTooltip";

const PAGE_SIZE = 50;

const fmtMin = (sec) => {
  const n = Math.max(0, Math.ceil((Number(sec) || 0) / 60));
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const fmtSec = (sec) => {
  const n = Math.max(0, parseInt(sec, 10) || 0);
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// "3m 45s" style, for the plain-English difference sentence in the Telephony explainer popup.
const fmtMinSecWords = (sec) => {
  const n = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(n / 60);
  const s = n % 60;
  if (m === 0) return `${s}s`;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

const STATUS_COLOR = {
  completed: "success",
  failed: "error",
  busy: "warning",
  "no-answer": "warning",
  no_answer: "warning",
  canceled: "default",
};

const ROLE_COLOR = {
  root: "primary",
  livekit: "info",
  transfer: "warning",
  leg: "default",
};

const getTransferSummaryLabel = (call) => {
  const legs = (Array.isArray(call?.legs) ? call.legs : [])
    .slice()
    .sort(
      (left, right) =>
        new Date(left?.startedAt || 0).getTime() -
        new Date(right?.startedAt || 0).getTime(),
    );
  const firstLeg = legs[0];
  const lastTransferLeg = legs
    .filter((leg) => leg?.role === "transfer" || leg?.isTransferLeg)
    .slice(-1)[0];

  if (!firstLeg || !lastTransferLeg) {
    return call?.rootCallSid || "—";
  }

  const fromNumber = firstLeg?.fromNumber || firstLeg?.from || "—";
  const toNumber = lastTransferLeg?.toNumber || lastTransferLeg?.to || "—";

  return `${fromNumber} → ${toNumber}`;
};

const CallsTableHead = ({ filter }) => (
  <TableHead>
    <TableRow>
      <TableCell />
      <TableCell>#</TableCell>
      <TableCell>Started</TableCell>
      <TableCell>Direction</TableCell>
      <TableCell>Status</TableCell>
      <TableCell>Duration</TableCell>
      <TableCell>Legs</TableCell>
      <TableCell>{filter === "transfers" ? "Transfer Path" : "Root SID"}</TableCell>
    </TableRow>
  </TableHead>
);

const CallRow = ({ call, idx, filter, timeZone = DEFAULT_PRACTICE_TIMEZONE, fixedTimezone, dimmed = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer", opacity: dimmed ? 0.6 : 1 }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{idx + 1}</TableCell>
        <TableCell>
          {call.startedAt ? formatShortInPracticeTimezone(call.startedAt, timeZone) : "—"}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={call.direction || "—"}
            color={call.direction === "inbound" ? "info" : call.direction === "outbound" ? "primary" : "default"}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={call.status || "—"}
            color={STATUS_COLOR[call.status] || "default"}
          />
        </TableCell>
        <TableCell>
          {(() => {
            const legs = Array.isArray(call.legs) ? call.legs : [];
            if (legs.length === 0) return fmtMin(call.durationSec);
            const mins = legs.reduce(
              (s, l) => s + Math.max(0, Math.ceil((Number(l.durationSec) || 0) / 60)),
              0,
            );
            return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
          })()}
        </TableCell>
        <TableCell>{call.legCount ?? call.legs?.length ?? 0}</TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontSize: 11, maxWidth: 220 }}>
          {/* BEGIN: Task 1 - Dashboard Transfers card summary label
              REVERT RULE ("revert transfer ui"):
              restore the previous Root SID / internal identifier text here. */}
          <span style={{ wordBreak: "break-all" }}>
            {filter === "transfers" ? getTransferSummaryLabel(call) : call.rootCallSid || "—"}
          </span>
          {/* END: Task 1 - Dashboard Transfers card summary label */}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: "action.hover" }}>
              <Typography variant="caption" color="text.secondary">
                Legs ({call.legs?.length ?? 0}) — total talk time {fmtSec(call.durationSec)}
                {call.transferSec > 0 && ` · transfer time ${fmtSec(call.transferSec)}`}
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>From → To</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>SID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(call.legs || []).map((leg, i) => (
                    <TableRow key={leg.sid || i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={leg.role || "leg"}
                          color={ROLE_COLOR[leg.role] || "default"}
                        />
                      </TableCell>
                      <TableCell sx={{ wordBreak: "break-all" }}>
                        {leg.from || "—"}
                        <span style={{ margin: "0 6px", opacity: 0.6 }}>→</span>
                        {leg.to || "—"}
                      </TableCell>
                      <TableCell>
                        {leg.startedAt ? (
                          <>
                            {formatShortInPracticeTimezone(leg.startedAt, fixedTimezone || timeZone)}
                            {fixedTimezone && fixedTimezone !== timeZone ? (
                              <div style={{ fontSize: 11, opacity: 0.65 }}>
                                {formatShortInPracticeTimezone(leg.startedAt, timeZone)} (your time)
                              </div>
                            ) : null}
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{fmtSec(leg.durationSec)}</TableCell>
                      <TableCell>{leg.status || "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 11, maxWidth: 180 }}>
                        <span style={{ wordBreak: "break-all" }}>{leg.sid || "—"}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(call.legs || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No legs</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LeadsTable = ({ leads, page }) => {
  const start = page * PAGE_SIZE;
  const slice = leads.slice(start, start + PAGE_SIZE);
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell>Contact Number</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {slice.map((lead, i) => (
          <TableRow key={`${lead.contactNumber}-${start + i}`}>
            <TableCell>{start + i + 1}</TableCell>
            <TableCell>{lead.contactNumber || "—"}</TableCell>
            <TableCell>
              {lead.status ? (
                <Chip size="small" label={lead.status} />
              ) : (
                <Typography variant="caption" color="text.secondary">no status</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
        {slice.length === 0 && (
          <TableRow>
            <TableCell colSpan={3} align="center">No leads in range</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

// Talk Time/AI/Transfer/Other split, each shown two ways: Actual (raw, unrounded seconds - what
// really happened) and Telephony/Billable (each leg individually rounded up to the nearest
// minute, matching real Twilio per-leg billing). "Other" only shows when nonzero - legs that are
// genuinely uncategorizable rather than a normal part of every call.
const CATEGORY_KEYS = ["talkTime", "ai", "transfer", "other"];
const CATEGORY_LABELS = { talkTime: "Talk time", ai: "AI", transfer: "Transfer", other: "Other" };
const CATEGORY_COLORS = {
  talkTime: "#B7B2A6",
  ai: "#A79CF0",
  transfer: "#4FBF9C",
  other: "#9AA7B5",
};
const categorizeLeg = (leg) => {
  if (leg?.legLevel === "root" || leg?.role === "root") return "talkTime";
  if (leg?.role === "sip-bridge" || leg?.role === "livekit") return "ai";
  if (leg?.role === "transfer") return "transfer";
  return "other";
};
// Telephony/billable is correct to add up every category - that's how Twilio actually bills
// each hop. Actual only counts real conversation time: the AI leg + the transfer leg (when one
// happened). Talk Time (the root/inbound leg) spans the whole call end-to-end and isn't extra
// time on top of those - it's the same conversation already counted once via the AI/transfer
// legs, just recorded again on the caller-facing leg.
const ACTUAL_CATEGORY_KEYS = ["ai", "transfer"];

// Talk Time is the reference (100% width). AI and Transfer are drawn as one continuous strip
// starting from the left (Transfer picks up exactly where AI ends) instead of being placed
// independently, so their combined length can be read directly against Talk Time's own bar.
// `onToggle`/`expanded` are only passed for the Talk Time row - clicking it shows/hides the
// AI/Transfer/Other rows below, so those are the only ones that get the chevron and pointer cursor.
const DurationBar = ({ label, color, seconds, startPct = 0, widthPct = 0, tooltip, onToggle, expanded }) => {
  const isClickable = typeof onToggle === "function";
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.25, cursor: isClickable ? "pointer" : "default" }}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-expanded={isClickable ? expanded : undefined}
      onClick={isClickable ? onToggle : undefined}
      onKeyDown={
        isClickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onToggle();
              }
            }
          : undefined
      }
    >
      {/* Reserved on every row, even non-clickable ones, so the label/track/value that follow
          start at the same x whether or not this particular row has a chevron. */}
      <Box sx={{ width: 20, flex: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isClickable ? (
          expanded ? (
            <KeyboardArrowDown fontSize="small" sx={{ color: "text.secondary" }} />
          ) : (
            <KeyboardArrowRight fontSize="small" sx={{ color: "text.secondary" }} />
          )
        ) : null}
      </Box>
      <Typography
        variant="body2"
        sx={{
          // Fixed (not min) so every row's label column is the exact same width regardless of
          // how long that row's own label happens to be - otherwise a longer label (like
          // "Connectivity") widens just its own column and knocks its track out of alignment
          // with the other rows. 116 comfortably fits "Connectivity" + the info icon on one line.
          width: 116,
          flex: "none",
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {tooltip ? <InfoTooltip text={tooltip} /> : null}
      </Typography>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          height: 14,
          borderRadius: 999,
          bgcolor: "rgba(27, 23, 37, 0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${startPct}%`,
            width: `${widthPct}%`,
            borderRadius: 999,
            bgcolor: color,
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ width: 44, flex: "none", textAlign: "right", fontWeight: 600 }}>
        {fmtSec(seconds)}
      </Typography>
    </Box>
  );
};

// Front face: the plain Telephony total, with a "Flip for breakdown" link. Back face: the same
// total broken into each category's own billed (per-leg, rounded up) minutes, added together -
// this is the actual Twilio-billing math, so seeing the parts is what tells you WHY the total
// looks the way it does.
const TelephonyFlipCard = ({ visibleCategoryKeys, durationBreakdown, totalBillableMin, onShowExample }) => {
  const [flipped, setFlipped] = useState(false);
  const toggle = () => setFlipped((v) => !v);
  const parts = visibleCategoryKeys.map((key) => ({
    key,
    label: CATEGORY_LABELS[key],
    color: CATEGORY_COLORS[key],
    billableMin: durationBreakdown[key].billableMin,
  }));

  return (
    <Box
      className={`calls-breakdown-flip${flipped ? " calls-breakdown-flip--flipped" : ""}`}
      role="button"
      tabIndex={0}
      aria-label="Flip between total telephony minutes and the per-leg breakdown"
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
    >
      <Box className="calls-breakdown-flip__inner">
        <Box className="calls-breakdown-flip__face calls-breakdown-stat-card">
          <Typography variant="caption" color="text.secondary">Telephony talk time</Typography>
          <Typography className="calls-breakdown-stat-card__value">{totalBillableMin} min</Typography>
          <span className="calls-breakdown-flip__link">
            <RotateCw size={13} strokeWidth={2.4} aria-hidden="true" />
            Flip for breakdown
          </span>
        </Box>
        <Box className="calls-breakdown-flip__face calls-breakdown-flip__face--back calls-breakdown-stat-card">
          <Typography variant="caption" color="text.secondary">Telephony talk time per leg</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ my: 1 }}>
            {parts.map((part, i) => (
              <React.Fragment key={part.key}>
                {i > 0 ? <Typography sx={{ opacity: 0.45 }}>+</Typography> : null}
                <Stack alignItems="center" spacing={0.25}>
                  <Box
                    sx={{
                      minWidth: 28,
                      textAlign: "center",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1.5,
                      fontWeight: 700,
                      bgcolor: part.color,
                      color: "#1b1725",
                    }}
                  >
                    {part.billableMin}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{part.label}</Typography>
                </Stack>
              </React.Fragment>
            ))}
            <Typography sx={{ opacity: 0.45 }}>=</Typography>
            <Stack alignItems="center" spacing={0.25}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>{totalBillableMin}</Typography>
              <Typography variant="caption" color="text.secondary">Total</Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Each leg rounded up to the next minute
          </Typography>
          {onShowExample ? (
            <button
              type="button"
              className="calls-breakdown-flip__link calls-breakdown-flip__link--example"
              onClick={(event) => {
                // Without this, the click bubbles up to the flip card's own onClick and flips
                // it back to the front face at the same moment the popup opens.
                event.stopPropagation();
                onShowExample();
              }}
            >
              Why is this higher?
            </button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

// Plain, static - the AI/Transfer/Other breakdown is expanded by clicking the Talk Time bar row
// above (see DurationBar's onToggle/expanded), not by clicking this card.
const ActualCard = ({ talkTimeSec }) => (
  <Box className="calls-breakdown-stat-card">
    <Typography variant="caption" color="text.secondary">Talk time</Typography>
    <Typography className="calls-breakdown-stat-card__value">{fmtSec(talkTimeSec)}</Typography>
    <Typography variant="caption" color="text.secondary">
      Wall clock the caller experienced
    </Typography>
  </Box>
);

// "Why is this higher?" popup: one example call, counted two ways - Talk time (the phases inside
// one connection) vs Telephony talk time (those same phases as separate billed legs, each rounded
// up on its own). Every bar in a column is drawn against the same ruler (0..talkTimeMin) so a
// bar's real position and length can be read directly, instead of each bar independently filling
// its own 0-100%. Fixed numbers, not pulled from live call data - a real call's leg durations
// aren't always populated (in-progress calls, older records), which produced an all-zero example;
// a stable worked example is what's actually useful for explaining the math to a client anyway.
const EXAMPLE_CALL_BREAKDOWN = {
  talkTimeSec: 135, // 2:15
  aiSec: 100, // 1:40
  transferSec: 20, // 0:20
  ringSec: 15, // 0:15 - AI + Transfer + ring (Connectivity) = Talk time (100 + 20 + 15 = 135)
  talkTimeMin: 3,
  aiMin: 2,
  transferMin: 1,
  // otherLeg: a leg that doesn't fit Talk time/AI/Transfer - a real, separately billed 4th leg
  // when a call has one (rare). This example call doesn't have one, hence 0.
  otherLegSec: 0,
  otherLegMin: 0,
  telephonyMin: 6, // 3 + 2 + 1 + 0
  legCount: 3,
};

const EXPLAINER_GRID_LINE = "rgba(27, 23, 37, 0.09)";

const explainerTrackSx = (maxMin) => ({
  backgroundImage: `repeating-linear-gradient(to right, ${EXPLAINER_GRID_LINE} 0, ${EXPLAINER_GRID_LINE} 1px, transparent 1px, transparent ${100 / maxMin}%)`,
});

const ExplainerRuler = ({ maxMin }) => {
  const ticks = Array.from({ length: maxMin + 1 }, (_, i) => i);
  return (
    <Box className="telephony-explainer-ruler">
      {ticks.map((m) => (
        <span key={m} className="telephony-explainer-ruler__tick" style={{ left: `${(m / maxMin) * 100}%` }}>
          {m === 0 ? "0" : `${m}m`}
        </span>
      ))}
    </Box>
  );
};

// A single phase positioned at its real [startSec, startSec + sec) offset within the call.
const ExplainerBar = ({ label, color, startSec, sec, maxMin, value }) => {
  const maxSec = maxMin * 60;
  const leftPct = maxSec > 0 ? (startSec / maxSec) * 100 : 0;
  const widthPct = maxSec > 0 ? Math.max((sec / maxSec) * 100, sec > 0 ? 1.5 : 0) : 0;
  return (
    <Box className="telephony-explainer-row">
      <Box className="telephony-explainer-row__head">
        <span className="telephony-explainer-row__label">{label}</span>
        <span className="telephony-explainer-row__value">{value}</span>
      </Box>
      <Box className="telephony-explainer-track" sx={explainerTrackSx(maxMin)}>
        <Box className="telephony-explainer-fill" sx={{ left: `${leftPct}%`, width: `${widthPct}%`, bgcolor: color }} />
      </Box>
    </Box>
  );
};

// A telephony leg: solid = the real duration, hatched = the padding added by rounding up to the
// next minute. Each leg is counted from its own zero, so it always starts at 0 here. `tooltip` is
// only used for the "Other leg" row - the one leg type whose meaning isn't self-evident from its
// name alone.
const ExplainerLegBar = ({ label, color, realSec, roundedMin, maxMin, tooltip }) => {
  const maxSec = maxMin * 60;
  const roundedSec = roundedMin * 60;
  const solidPct = maxSec > 0 ? (realSec / maxSec) * 100 : 0;
  const dashPct = maxSec > 0 ? Math.max(((roundedSec - realSec) / maxSec) * 100, 0) : 0;
  return (
    <Box className="telephony-explainer-row">
      <Box className="telephony-explainer-row__head">
        <span className="telephony-explainer-row__label">
          {label}
          {tooltip ? (
            <span className="telephony-explainer-row__info">
              <InfoTooltip text={tooltip} />
            </span>
          ) : null}
        </span>
        <span className="telephony-explainer-row__value">
          {fmtSec(realSec)} <span className="telephony-explainer-row__arrow">&rarr;</span> {roundedMin}
        </span>
      </Box>
      <Box className="telephony-explainer-track" sx={explainerTrackSx(maxMin)}>
        <Box className="telephony-explainer-fill" sx={{ left: 0, width: `${solidPct}%`, bgcolor: color }} />
        {dashPct > 0 ? (
          <Box
            className="telephony-explainer-fill"
            sx={{
              left: `${solidPct}%`,
              width: `${dashPct}%`,
              backgroundImage: `repeating-linear-gradient(115deg, ${color} 0 3px, transparent 3px 6px)`,
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
};

const TelephonyExplainerDialog = ({ open, onClose, example }) => {
  if (!example) return null;
  const rulerMaxMin = Math.max(example.talkTimeMin, 1);
  const diffSec = Math.max(0, example.telephonyMin * 60 - example.talkTimeSec);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Why are Telephony talk time mins higher than Talk time mins?
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Here is one example call, counted two ways.
        </Typography>
        <Box className="telephony-explainer-grid">
          <Box className="telephony-explainer-col">
            <Typography className="telephony-explainer-col__title" sx={{ mb: "19px" }}>Talk time mins</Typography>
            <Typography className="telephony-explainer-col__desc" sx={{ mb: "28px" }}>
              One call. Talk time <b>contains</b> the phases inside it.
            </Typography>
            <ExplainerRuler maxMin={rulerMaxMin} />
            <ExplainerBar
              label="Talk time"
              color={CATEGORY_COLORS.talkTime}
              startSec={0}
              sec={example.talkTimeSec}
              maxMin={rulerMaxMin}
              value={fmtSec(example.talkTimeSec)}
            />
            <Box className="telephony-explainer-madeup">
              <span className="telephony-explainer-madeup__label">made up of</span>
              <ExplainerBar
                label="AI"
                color={CATEGORY_COLORS.ai}
                startSec={0}
                sec={example.aiSec}
                maxMin={rulerMaxMin}
                value={fmtSec(example.aiSec)}
              />
              <ExplainerBar
                label="Transfer"
                color={CATEGORY_COLORS.transfer}
                startSec={example.aiSec}
                sec={example.transferSec}
                maxMin={rulerMaxMin}
                value={fmtSec(example.transferSec)}
              />
              <ExplainerBar
                label="Connectivity"
                color={CATEGORY_COLORS.other}
                startSec={example.aiSec + example.transferSec}
                sec={example.ringSec}
                maxMin={rulerMaxMin}
                value={fmtSec(example.ringSec)}
              />
            </Box>
            <Typography className="telephony-explainer-footnote" sx={{ mt: "16px" }}>
              AI + Transfer + Connectivity = {fmtSec(example.talkTimeSec)}.
            </Typography>
          </Box>

          <Box className="telephony-explainer-col">
            <Typography
              className="telephony-explainer-col__title telephony-explainer-col__title--hero"
              sx={{ mb: "19px" }}
            >
              Telephony talk time mins
            </Typography>
            <Typography className="telephony-explainer-col__desc" sx={{ mb: "28px" }}>
              <b>{example.legCount} separate legs.</b> Each is its own connection, counted from its own zero.
            </Typography>
            <ExplainerRuler maxMin={rulerMaxMin} />
            <ExplainerLegBar
              label="Talk time leg"
              color={CATEGORY_COLORS.talkTime}
              realSec={example.talkTimeSec}
              roundedMin={example.talkTimeMin}
              maxMin={rulerMaxMin}
            />
            <ExplainerLegBar
              label="AI leg"
              color={CATEGORY_COLORS.ai}
              realSec={example.aiSec}
              roundedMin={example.aiMin}
              maxMin={rulerMaxMin}
            />
            <ExplainerLegBar
              label="Transfer leg"
              color={CATEGORY_COLORS.transfer}
              realSec={example.transferSec}
              roundedMin={example.transferMin}
              maxMin={rulerMaxMin}
            />
            <ExplainerLegBar
              label="Other leg"
              color={CATEGORY_COLORS.other}
              realSec={example.otherLegSec}
              roundedMin={example.otherLegMin}
              maxMin={rulerMaxMin}
              tooltip="A leg that doesn't fit Talk time, AI, or Transfer. When a call has one, it's counted and rounded up the same way as any other leg. This example call doesn't have one."
            />
            <Typography className="telephony-explainer-footnote" sx={{ mt: "16px" }}>
              Connectivity is counted inside the Talk time leg, not counted again on its own. An Other leg is added only when a call has a connection like that. This example doesn't have one. {example.talkTimeMin} + {example.aiMin} + {example.transferMin} + {example.otherLegMin} = {example.telephonyMin} min.
            </Typography>
          </Box>

          <Box className="telephony-explainer-totals">
            <Typography className="telephony-explainer-col__title" sx={{ mb: "19px" }}>This call, totalled</Typography>
            <Box className="telephony-explainer-total-box">
              <span className="telephony-explainer-total-box__cap">Talk time mins</span>
              <span className="telephony-explainer-total-box__num">{fmtSec(example.talkTimeSec)}</span>
              <span className="telephony-explainer-total-box__sub">1 connection</span>
            </Box>
            <Box className="telephony-explainer-total-box telephony-explainer-total-box--hero">
              <span className="telephony-explainer-total-box__cap">Telephony talk time mins</span>
              <span className="telephony-explainer-total-box__num">{example.telephonyMin} min</span>
              <span className="telephony-explainer-total-box__sub">{example.legCount} legs, each rounded up</span>
            </Box>
            <Typography className="telephony-explainer-diff" sx={{ mt: "2px" }}>
              The difference of <b>{fmtMinSecWords(diffSec)}</b> isn't extra time spent. It's what happens when{" "}
              {example.legCount} separate connections are each rounded up on their own, instead of the whole call
              being rounded up just once.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const TITLES = {
  all: "All Calls",
  billable: "Call Duration",
  completed: "Completed Calls",
  leads: "Leads",
  transfers: "Transfers",

  // BEGIN: Adding Failed and Missed calls clickable keys to match StatusBreakdown's clickable cards
  failed: "Failed Calls",
  missed: "Missed Calls",
  // END: Adding Failed and Missed calls clickable keys to match StatusBreakdown's clickable cards

  // Remaining Call Status Overview rows (Dashboard.jsx statusListItems) made clickable the same way
  voicemail: "Voicemail Calls",
  noAnswer: "No Answer Calls",
  busy: "Busy Calls",
  canceled: "Cancelled Calls",
  other: "Other Calls",
};

const CallsBreakdownDialog = ({
  open,
  onClose,
  filter,
  direction = "both",
  calls = [],
  leads = [],
  totals,
  timeZone = DEFAULT_PRACTICE_TIMEZONE,
  // fixedTimezone: the practice's own configured timezone (not the viewer's) - shown alongside
  // timeZone (now the viewer's own local/effective zone) for real call-leg timestamps
  // specifically, since a call physically happened at a specific practice-local moment
  // regardless of who's looking at it. Defaults to timeZone so a caller that doesn't pass this
  // shows no difference at all - purely additive.
  fixedTimezone,
  // groupByConnection: used by the per-lead "Call Details" view (InBoundCalls.jsx/LeadGGrid.jsx)
  // to split the list into Connected Calls vs Unconnected Attempts, since a client seeing e.g.
  // "20 calls" but only 5 transcripts otherwise reads as broken data rather than "most attempts
  // never connected." Left off for Dashboard's own breakdown dialog, which already scopes to one
  // status bucket per open (so the split would just be one empty section).
  groupByConnection = false,
  // ambiguousNote: an optional caption shown under the title - used for the one case (leads
  // sharing a phone number AND uploaded in the exact same batch) where this call history can't
  // be reliably attributed to just this lead (see services/index.js's CallHistoryMayBeShared).
  ambiguousNote = "",
}) => {
  const resolvedFixedTimezone = fixedTimezone || timeZone;
  const [page, setPage] = useState(0);
  const [showUnconnected, setShowUnconnected] = useState(false);
  const [showLegBreakdown, setShowLegBreakdown] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  const filteredCalls = useMemo(() => {
    let list = calls;
    if (direction === "inbound") list = list.filter((c) => c.direction === "inbound");
    else if (direction === "outbound") list = list.filter((c) => c.direction === "outbound");
    if (filter === "completed") list = list.filter((c) => c.status === "completed");

    // BEGIN: Adding Failed and Missed calls clickable keys to match StatusBreakdown's clickable cards
    // NOTE: this classification must stay in sync with Dashboard.jsx's directedStatusCounts -
    // both compute the same 7 mutually-exclusive buckets from the same status/rawStatus/direction
    // fields, so a card's displayed count and this dialog's "Matching Calls" list agree.
    else if (filter === "failed") list = list.filter((c) => c.status === "failed");
    else if (filter === "missed")
      // Was previously checking c.status === "no-answer" (hyphenated) - the actual status
      // category value is "no_answer" (underscore, see getStatusCategoryFromValue), so this
      // filter was silently only ever matching the "busy" half of inbound missed calls.
      list = list.filter(
        (c) => c.direction === "inbound" && (c.status === "no_answer" || c.rawStatus === "busy")
      );
    else if (filter === "voicemail") list = list.filter((c) => c.status === "voicemail");
    else if (filter === "noAnswer")
      list = list.filter((c) => c.direction !== "inbound" && c.status === "no_answer");
    else if (filter === "busy")
      list = list.filter((c) => c.direction !== "inbound" && c.rawStatus === "busy");
    else if (filter === "canceled") list = list.filter((c) => c.rawStatus === "canceled");
    else if (filter === "other")
      list = list.filter((c) => {
        const isInboundMissed =
          c.direction === "inbound" && (c.status === "no_answer" || c.rawStatus === "busy");
        if (isInboundMissed || c.rawStatus === "canceled") return false;
        if (["completed", "voicemail", "failed", "no_answer"].includes(c.status)) return false;
        if (c.rawStatus === "busy") return false;
        return true;
      });
    // END: Adding Failed and Missed calls clickable keys to match StatusBreakdown's clickable cards

    else if (filter === "transfers") {
      list = list.filter((c) => Number(c.transferLegCount || 0) > 0);
    }

    // Newest call first - list came back in whatever order the backend returned it in, not
    // necessarily sorted. .slice() first even when a filter already ran above (producing a
    // fresh array) so the "no filter matched" fallthrough case (list still === calls) never
    // sorts the caller's own array in place.
    return list.slice().sort(
      (left, right) => new Date(right?.startedAt || 0).getTime() - new Date(left?.startedAt || 0).getTime(),
    );
  }, [filter, direction, calls]);

  // "Connected" = the call actually reached someone/something on the other end and Twilio
  // billed for real connected time - status "completed" (a person answered) or "voicemail"
  // (Twilio's own answering-machine detection picked up the call; it still has real duration
  // and was billed same as any other connected call). Everything else (missed, busy, failed,
  // canceled) never connected, so Twilio never billed real call time for it either.
  const connectedCalls = useMemo(
    () => filteredCalls.filter((c) => c.status === "completed" || c.status === "voicemail"),
    [filteredCalls],
  );
  const unconnectedCalls = useMemo(
    () => filteredCalls.filter((c) => c.status !== "completed" && c.status !== "voicemail"),
    [filteredCalls],
  );

  // Adding Failed and Missed calls clickable keys to match StatusBreakdown's clickable cards
  const usesFilteredCount =
    filter === "transfers" ||
    filter === "failed" ||
    filter === "missed" ||
    filter === "voicemail" ||
    filter === "noAnswer" ||
    filter === "busy" ||
    filter === "canceled" ||
    filter === "other";
  const summaryCountLabel =
    filter === "transfers" ? "Transfer Calls Total" : usesFilteredCount ? "Matching Calls" : "Total Calls";
  const summaryCountValue = usesFilteredCount ? filteredCalls.length : (totals?.total ?? 0);
  // END: Adding Failed and Missed calls clickable keys to match StatusBreakdown's clickable cards

  const isLeads = filter === "leads";
  const dataLength = isLeads ? leads.length : filteredCalls.length;
  // const summaryCountLabel =
  //   filter === "transfers" ? "Transfer Calls Total" : "Total Calls";
  // const summaryCountValue =
  //   filter === "transfers" ? filteredCalls.length : (totals?.total ?? 0);

  const durationBreakdown = useMemo(() => {
    const totals = {
      talkTime: { actualSec: 0, billableMin: 0 },
      ai: { actualSec: 0, billableMin: 0 },
      transfer: { actualSec: 0, billableMin: 0 },
      other: { actualSec: 0, billableMin: 0 },
    };
    filteredCalls.forEach((call) => {
      const legs = Array.isArray(call.legs) ? call.legs : [];
      if (legs.length === 0) {
        // No per-leg breakdown available for this call - it's all Talk Time by definition
        // (durationSec already represents just the primary/root leg - see services/index.js).
        const sec = Number(call.durationSec) || 0;
        totals.talkTime.actualSec += sec;
        totals.talkTime.billableMin += Math.max(0, Math.ceil(sec / 60));
        return;
      }
      legs.forEach((leg) => {
        const sec = Number(leg.durationSec) || 0;
        const bucket = totals[categorizeLeg(leg)];
        bucket.actualSec += sec;
        bucket.billableMin += Math.max(0, Math.ceil(sec / 60));
      });
    });
    return totals;
  }, [filteredCalls]);

  const visibleCategoryKeys = CATEGORY_KEYS.filter(
    (key) => key !== "other" || durationBreakdown.other.actualSec > 0,
  );
  const durationBreakdownTotals = visibleCategoryKeys.reduce(
    (acc, key) => ({
      actualSec:
        acc.actualSec + (ACTUAL_CATEGORY_KEYS.includes(key) ? durationBreakdown[key].actualSec : 0),
      billableMin: acc.billableMin + durationBreakdown[key].billableMin,
    }),
    { actualSec: 0, billableMin: 0 },
  );

  // Bars: Talk Time is the 100% reference. "Other" here is display-only - Talk Time minus AI
  // minus Transfer, which is mostly ring/unanswered time (a call with no leg breakdown at all,
  // e.g. no-answer/busy/failed, counts its whole ring duration only toward Talk Time - see
  // durationBreakdown above) - so AI + Transfer + Other always add up to exactly Talk Time, with
  // no leftover gap on the bar. This does NOT change durationBreakdown.other itself, which the
  // Telephony flip card's billed-minutes equation still uses unmodified.
  const talkTimeSec = durationBreakdown.talkTime.actualSec;
  const aiSec = durationBreakdown.ai.actualSec;
  const transferSec = durationBreakdown.transfer.actualSec;
  const otherSec = Math.max(0, talkTimeSec - aiSec - transferSec);
  const pctOfTalkTime = (sec) =>
    talkTimeSec > 0 ? Math.min(100, (Math.max(0, sec) / talkTimeSec) * 100) : 0;
  const aiPct = pctOfTalkTime(aiSec);
  const transferPct = pctOfTalkTime(transferSec);
  const otherPct = pctOfTalkTime(otherSec);

  const pageCount = Math.max(1, Math.ceil(dataLength / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const pageSlice = isLeads ? null : filteredCalls.slice(start, start + PAGE_SIZE);

  React.useEffect(() => {
    setPage(0);
  }, [filter, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          m: 2,
          top: "auto !important",
          left: "auto !important",
          position: "relative !important",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center !important",
          justifyContent: "center !important",
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {TITLES[filter] || "Calls"}
        {groupByConnection ? (
          <InfoTooltip text="Connected calls include calls answered by a person or by voicemail. Missed, busy, failed, or canceled attempts never connected." />
        ) : null}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {ambiguousNote ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 2,
              p: 1,
              borderRadius: 1,
              bgcolor: "warning.light",
              color: "warning.contrastText",
            }}
          >
            {ambiguousNote}
          </Typography>
        ) : null}
        {!isLeads && totals && (
          <>
            <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap">
              <Stack>
                <Typography variant="caption" color="text.secondary">{summaryCountLabel}</Typography>
                <Typography variant="body1">{summaryCountValue}</Typography>
              </Stack>
            </Stack>
            <Box sx={{ mb: 3 }}>
              <DurationBar
                label={CATEGORY_LABELS.talkTime}
                color={CATEGORY_COLORS.talkTime}
                seconds={talkTimeSec}
                startPct={0}
                widthPct={100}
                onToggle={() => setShowLegBreakdown((v) => !v)}
                expanded={showLegBreakdown}
              />
              <Collapse in={showLegBreakdown} timeout="auto" unmountOnExit>
                <Box sx={{ pt: 1 }}>
                  <DurationBar
                    label={CATEGORY_LABELS.ai}
                    color={CATEGORY_COLORS.ai}
                    seconds={aiSec}
                    startPct={0}
                    widthPct={aiPct}
                  />
                  <DurationBar
                    label={CATEGORY_LABELS.transfer}
                    color={CATEGORY_COLORS.transfer}
                    seconds={transferSec}
                    startPct={aiPct}
                    widthPct={transferPct}
                  />
                  {otherSec > 0 ? (
                    <DurationBar
                      label="Connectivity"
                      color={CATEGORY_COLORS.other}
                      seconds={otherSec}
                      startPct={aiPct + transferPct}
                      widthPct={otherPct}
                      tooltip="Includes time spent ringing before pickup, plus the time before AI or Transfer actually connects."
                    />
                  ) : null}
                </Box>
              </Collapse>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  mt: 2,
                }}
              >
                <ActualCard talkTimeSec={talkTimeSec} />
                <TelephonyFlipCard
                  visibleCategoryKeys={visibleCategoryKeys}
                  durationBreakdown={durationBreakdown}
                  totalBillableMin={durationBreakdownTotals.billableMin}
                  onShowExample={() => setExplainerOpen(true)}
                />
              </Box>
            </Box>
          </>
        )}

        <TelephonyExplainerDialog
          open={explainerOpen}
          onClose={() => setExplainerOpen(false)}
          example={EXAMPLE_CALL_BREAKDOWN}
        />

        {isLeads ? (
          <LeadsTable leads={leads} page={safePage} />
        ) : groupByConnection ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Connected Calls ({connectedCalls.length})
            </Typography>
            <Table size="small" stickyHeader sx={{ mb: connectedCalls.length ? 3 : 1 }}>
              <CallsTableHead filter={filter} />
              <TableBody>
                {connectedCalls.map((call, i) => (
                  <CallRow
                    key={call.rootCallSid || i}
                    call={call}
                    idx={i}
                    filter={filter}
                    timeZone={timeZone}
                    fixedTimezone={resolvedFixedTimezone}
                  />
                ))}
                {connectedCalls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No connected calls</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {unconnectedCalls.length > 0 ? (
              <>
                <Button
                  size="small"
                  onClick={() => setShowUnconnected((v) => !v)}
                  startIcon={showUnconnected ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                  sx={{ mb: 1 }}
                >
                  {showUnconnected ? "Hide" : "Show"} Unconnected Attempts ({unconnectedCalls.length})
                </Button>
                <Collapse in={showUnconnected} timeout="auto" unmountOnExit>
                  <Table size="small" stickyHeader>
                    <CallsTableHead filter={filter} />
                    <TableBody>
                      {unconnectedCalls.map((call, i) => (
                        <CallRow
                          key={call.rootCallSid || i}
                          call={call}
                          idx={i}
                          filter={filter}
                          timeZone={timeZone}
                          fixedTimezone={resolvedFixedTimezone}
                          dimmed
                        />
                      ))}
                    </TableBody>
                  </Table>
                </Collapse>
              </>
            ) : null}
          </>
        ) : (
          <Table size="small" stickyHeader>
            <CallsTableHead filter={filter} />
            <TableBody>
              {pageSlice.map((call, i) => (
                <CallRow
                  key={call.rootCallSid || start + i}
                  call={call}
                  idx={start + i}
                  filter={filter}
                  timeZone={timeZone}
                  fixedTimezone={resolvedFixedTimezone}
                />
              ))}
              {pageSlice.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">No calls match this filter</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          {groupByConnection
            ? `${dataLength} call${dataLength === 1 ? "" : "s"} total`
            : dataLength === 0
              ? "No results"
              : `Showing ${start + 1}–${Math.min(start + PAGE_SIZE, dataLength)} of ${dataLength}`}
        </Typography>
        <Stack direction="row" spacing={1}>
          {!groupByConnection ? (
            <>
              <Button
                disabled={safePage <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </Button>
              <Button
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next
              </Button>
            </>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CallsBreakdownDialog;


